import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';

import { Html } from '@react-three/drei';
import { ThreeEvent, useThree } from '@react-three/fiber';
import _ from 'lodash';
import noop from 'lodash/noop';
import { X } from 'lucide-react';
import * as THREE from 'three';

import { useAppSelector } from '../../../hooks';
import {
  ActualValue,
  ExpectedValue,
  MeasurementData,
  TargetFunction,
} from '../../../store/reducers/workspace.reducer.ts';
import { getUser } from '../../../store/selectors/user.selector.ts';
import { store } from '../../../store/store.ts';
import { UnitSystem } from '../../../types/util.ts';
import { MEASUREMENT_VISUALIZATION_FIELD_MAP, MeasurementVisualizationType } from '../../../types/view';
import { getLengthUnitLabel, getPhotometricUnit, toDisplayLength } from '../../../utils/unitSystem.ts';
import {
  DisplayScale,
  FIELD_INDEX_MAP,
  calculateDisplayScale,
  decompressMeasurementData,
  getThreeColorAtPosition,
} from './measurementUtils';

interface Measurement {
  L: number | null;
  E: number | null;
  Ev: number | null;
  Esc: number | null;
  Ehs: number | null;
}

interface Point {
  position: {
    x: number;
    y: number;
    z: number;
  };
  measurement: Measurement;
  layerName: string;
  normId: number | null;
  expected: {
    // only for Taman Score calculation
    L: number | null;
    E: number | null;
    Ev: number | null;
    Esc: number | null;
    Ehs: number | null;
  };
  ignored: boolean;
}

interface LayerMeasurementData {
  name: string;
  uuid: string;
  normId: number | null;
  L: number | null;
  Uo: number | null;
  Ul: number | null;
  Uow: number | null;
  fTI: number | null;
  REI: number | null;
  E: number | null;
  Ev: number | null;
  Esc: number | null;
  Ehs: number | null;
  points: Point[];
}

interface GroupedMeasurementData {
  [name: string]: LayerMeasurementData[];
}

const parseAndGroupMeasurementData = (data: MeasurementData[] | null | undefined): GroupedMeasurementData => {
  const grouped: GroupedMeasurementData = {};
  if (!Array.isArray(data) || data.length === 0) {
    return grouped;
  }
  data.forEach((layerMeasurement) => {
    try {
      // Decode and decompress positions data
      let positions: number[][] = [];
      if (layerMeasurement.positions) {
        positions = decompressMeasurementData<number[][]>(layerMeasurement.positions);
      }

      // Decode and decompress measurements data
      let measurements: number[][] = [];
      if (layerMeasurement.measurements) {
        measurements = decompressMeasurementData<number[][]>(layerMeasurement.measurements);
      }

      let ignoredPoints: number[] = [];
      if (layerMeasurement.ignoredPoints) {
        ignoredPoints = decompressMeasurementData<number[]>(layerMeasurement.ignoredPoints);
      }

      // Create points array from positions and measurements
      const points: Point[] = [];
      for (let i = 0; i < positions.length; i++) {
        const position = positions[i];
        const measurement = measurements[i];
        const ignored = ignoredPoints && ignoredPoints[i] === 1 ? true : false;

        points.push({
          position: {
            x: position[0],
            y: position[1],
            z: position[2],
          },
          measurement: {
            L: measurement[FIELD_INDEX_MAP.L] !== -1.0 ? measurement[FIELD_INDEX_MAP.L] : null,
            E: measurement[FIELD_INDEX_MAP.E] !== -1.0 ? measurement[FIELD_INDEX_MAP.E] : null,
            Ev: measurement[FIELD_INDEX_MAP.Ev] !== -1.0 ? measurement[FIELD_INDEX_MAP.Ev] : null,
            Esc: measurement[FIELD_INDEX_MAP.Esc] !== -1.0 ? measurement[FIELD_INDEX_MAP.Esc] : null,
            Ehs: measurement[FIELD_INDEX_MAP.Ehs] !== -1.0 ? measurement[FIELD_INDEX_MAP.Ehs] : null,
          },
          layerName: layerMeasurement.name,
          normId: layerMeasurement.normId,
          expected: {
            L: getExpectedValue('L', layerMeasurement),
            E: getExpectedValue('E', layerMeasurement),
            Ev: getExpectedValue('Ev', layerMeasurement),
            Esc: getExpectedValue('Esc', layerMeasurement),
            Ehs: getExpectedValue('Ehs', layerMeasurement),
          },
          ignored: ignored,
        });
      }

      // Create parsed item with actual values
      const parsedItem: LayerMeasurementData = {
        name: layerMeasurement.name,
        uuid: layerMeasurement.uuid,
        normId: layerMeasurement.normId,
        L: getActualValue('L', layerMeasurement),
        Uo: getActualValue('Uo', layerMeasurement),
        Ul: getActualValue('Ul', layerMeasurement),
        Uow: getActualValue('Uow', layerMeasurement),
        fTI: getActualValue('fTI', layerMeasurement),
        REI: getActualValue('REI', layerMeasurement),
        E: getActualValue('E', layerMeasurement),
        Ev: getActualValue('Ev', layerMeasurement),
        Esc: getActualValue('Esc', layerMeasurement),
        Ehs: getActualValue('Ehs', layerMeasurement),
        points: points,
      };

      if (!grouped[parsedItem.name]) grouped[parsedItem.name] = [];
      grouped[parsedItem.name].push(parsedItem);
    } catch (error) {
      console.error('Error parsing measurement data:', error, layerMeasurement);
    }
  });
  return grouped;
};

const getExpectedValue = (fn: TargetFunction, measurement: MeasurementData): number | null => {
  return findValue(measurement.expectedValues, fn);
};

const getActualValue = (fn: TargetFunction, measurement: MeasurementData): number | null => {
  return findValue(measurement.actualValues, fn);
};

const findValue = (collection: ExpectedValue[] | ActualValue[], fn: TargetFunction): number | null => {
  const average = _.findLast(collection, (v) => v.function === fn && v.type === 'AVERAGE');
  if (average) return average.value;
  const exact = _.findLast(collection, (v) => v.function === fn && v.type === 'EXACT');
  if (exact) return exact.value;
  const min = _.findLast(collection, (v) => v.function === fn && v.type === 'MIN');
  if (min) return min.value;
  const max = _.findLast(collection, (v) => v.function === fn && v.type === 'MAX');
  if (max) return max.value;
  return null;
};

const getTamanScoreColor = (point: Point): string => {
  let actualValue: number | null = null;
  let expectedValue: number | null = null;

  // Check each measurement type in order (Taman Score - deviation from expected)
  if (point.measurement.L !== null) {
    actualValue = point.measurement.L;
    expectedValue = point.expected.L;
  } else if (point.measurement.E !== null) {
    actualValue = point.measurement.E;
    expectedValue = point.expected.E;
  } else if (point.measurement.Ev !== null) {
    actualValue = point.measurement.Ev;
    expectedValue = point.expected.Ev;
  } else if (point.measurement.Esc !== null) {
    actualValue = point.measurement.Esc;
    expectedValue = point.expected.Esc;
  } else if (point.measurement.Ehs !== null) {
    actualValue = point.measurement.Ehs;
    expectedValue = point.expected.Ehs;
  }

  // If both actual and expected values exist, compare them
  if (actualValue !== null && expectedValue !== null && expectedValue > 0) {
    const percentDifference = Math.abs((actualValue - expectedValue) / expectedValue);
    const green = new THREE.Color(0x00ff00);
    const red = new THREE.Color(0xff0000);
    const color = new THREE.Color().lerpColors(green, red, percentDifference);
    return '#' + color.getHexString();
  }

  // If no comparison can be made, display gray
  return '#404040';
};

// Get color for basic visualization mode (global scale based on value)
const getBasicVisualizationColor = (
  point: Point,
  fieldKey: 'L' | 'E' | 'Ev' | 'Esc' | 'Ehs',
  minValue: number,
  maxValue: number,
): string => {
  const value = point.measurement[fieldKey];

  if (value === null || maxValue === minValue) {
    return '#404040'; // Gray for missing values
  }

  // Normalize value to 0-1 range
  const normalizedValue = (value - minValue) / (maxValue - minValue);

  // Use shared color gradient function
  const color = getThreeColorAtPosition(normalizedValue);
  return '#' + color.getHexString();
};

const getIlluminanceColor = (
  point: Point,
  visualizationType: MeasurementVisualizationType,
  minMaxValues?: { min: number; max: number },
): string => {
  // Taman Score - deviation from expected
  if (visualizationType === 'tamanScore') {
    return getTamanScoreColor(point);
  }

  // Basic visualization modes - use global scale
  const fieldKey = MEASUREMENT_VISUALIZATION_FIELD_MAP[visualizationType];
  if (fieldKey && minMaxValues) {
    return getBasicVisualizationColor(point, fieldKey, minMaxValues.min, minMaxValues.max);
  }

  return '#404040';
};

const POPUP_FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Roboto, "Helvetica Neue", Arial, sans-serif';

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      gridColumn: '1 / -1',
      color: 'rgba(255, 255, 255, 0.45)',
      fontSize: '9.5px',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      marginBottom: '2px',
    }}
  >
    {children}
  </div>
);

const KeyCell = ({ children }: { children: React.ReactNode }) => (
  <span style={{ color: 'rgba(255, 255, 255, 0.55)', fontWeight: 500 }}>{children}</span>
);

const ValueCell = ({ children, accent = false }: { children: React.ReactNode; accent?: boolean }) => (
  <span
    style={{
      color: accent ? '#FF8A8A' : 'rgba(255, 255, 255, 0.95)',
      fontVariantNumeric: 'tabular-nums',
      textAlign: 'right',
    }}
  >
    {children}
  </span>
);

const MeasurementPopup = ({
  point,
  onClose,
  unitSystem,
}: {
  point: Point;
  onClose: () => void;
  unitSystem: UnitSystem;
}) => {
  const { t } = useTranslation();
  const lengthUnitLabel = getLengthUnitLabel(unitSystem);
  const { position, measurement } = point;
  return (
    <div
      style={{
        background: 'rgba(18, 18, 20, 0.82)',
        backdropFilter: 'blur(16px) saturate(160%)',
        WebkitBackdropFilter: 'blur(16px) saturate(160%)',
        color: 'rgba(255, 255, 255, 0.95)',
        padding: '10px 12px 10px 12px',
        borderRadius: '10px',
        fontSize: '11.5px',
        fontFamily: POPUP_FONT_STACK,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        minWidth: '190px',
        pointerEvents: 'auto',
        transform: 'translate(12px, -50%)',
        position: 'relative',
      }}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        style={{
          position: 'absolute',
          top: '6px',
          right: '6px',
          background: 'rgba(255, 255, 255, 0.04)',
          border: 'none',
          color: 'rgba(255, 255, 255, 0.5)',
          cursor: 'pointer',
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '6px',
          transition: 'background 120ms ease, color 120ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = 'rgba(255, 255, 255, 0.95)';
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)';
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
        }}
      >
        <X size={12} />
      </button>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          rowGap: '3px',
          columnGap: '16px',
          paddingRight: '20px',
        }}
      >
        <SectionLabel>
          {t('measurement.position')} ({lengthUnitLabel})
        </SectionLabel>
        <KeyCell>x</KeyCell>
        <ValueCell>{toDisplayLength(position.x, unitSystem).toFixed(2)}</ValueCell>
        <KeyCell>y</KeyCell>
        <ValueCell>{toDisplayLength(position.y, unitSystem).toFixed(2)}</ValueCell>
        <KeyCell>z</KeyCell>
        <ValueCell>{toDisplayLength(position.z, unitSystem).toFixed(2)}</ValueCell>
      </div>

      <div
        style={{
          height: '1px',
          background: 'rgba(255, 255, 255, 0.08)',
          margin: '8px 0',
        }}
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          rowGap: '3px',
          columnGap: '16px',
        }}
      >
        <SectionLabel>{t('measurement.measurementLabel')}</SectionLabel>
        {measurement.L !== null && (
          <>
            <KeyCell>L</KeyCell>
            <ValueCell>
              {measurement.L.toFixed(2)} {getPhotometricUnit('L')}
            </ValueCell>
          </>
        )}
        {measurement.E !== null && (
          <>
            <KeyCell>E</KeyCell>
            <ValueCell>
              {measurement.E.toFixed(2)} {getPhotometricUnit('E')}
            </ValueCell>
          </>
        )}
        {measurement.Ev !== null && (
          <>
            <KeyCell>Ev</KeyCell>
            <ValueCell>
              {measurement.Ev.toFixed(2)} {getPhotometricUnit('Ev')}
            </ValueCell>
          </>
        )}
        {measurement.Esc !== null && (
          <>
            <KeyCell>Esc</KeyCell>
            <ValueCell>
              {measurement.Esc.toFixed(2)} {getPhotometricUnit('Esc')}
            </ValueCell>
          </>
        )}
        {measurement.Ehs !== null && (
          <>
            <KeyCell>Ehs</KeyCell>
            <ValueCell>
              {measurement.Ehs.toFixed(2)} {getPhotometricUnit('Ehs')}
            </ValueCell>
          </>
        )}
        {point.ignored && (
          <>
            <KeyCell>
              <span style={{ color: '#FF8A8A' }}>{t('measurement.ignored')}</span>
            </KeyCell>
            <ValueCell accent>{t('measurement.yes')}</ValueCell>
          </>
        )}
      </div>
    </div>
  );
};

const makeInstanced = (
  points: Point[],
  visualizationType: MeasurementVisualizationType,
  scale: DisplayScale | null,
  filterRange: { min: number | null; max: number | null } | null,
): THREE.InstancedMesh => {
  const geometry = new THREE.SphereGeometry(0.1, 14, 14);
  // MeshBasicMaterial respects per-instance vertex colors faithfully — no lighting/emissive
  // washes out the gradient. Gives crisp, saturated dots typical of modern data-viz.
  const material = new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: 0.92,
    toneMapped: false,
  });
  const count = points.length;
  const mesh = new THREE.InstancedMesh(geometry, material, count);
  const matrix = new THREE.Matrix4();
  const hiddenMatrix = new THREE.Matrix4().makeScale(0, 0, 0);

  mesh.frustumCulled = false;
  // Tag so the scene's native click handler (useSceneInteraction) can detect a
  // direct hit on a measurement point and avoid selecting the layer behind it.
  mesh.userData.isMeasurementPoint = true;

  const minMaxValues = visualizationType !== 'tamanScore' && scale ? { min: scale.min, max: scale.max } : undefined;

  // Filter applies to value-based visualizations only (skipped for tamanScore).
  const fieldKey = visualizationType !== 'tamanScore' ? MEASUREMENT_VISUALIZATION_FIELD_MAP[visualizationType] : null;
  const applyFilter = filterRange !== null && fieldKey !== null;

  points.forEach((point, i) => {
    const value = fieldKey ? point.measurement[fieldKey] : null;
    // Each edge of the filter is independently nullable: null means "no
    // constraint on this side", so values past that edge stay visible.
    const inRange =
      !applyFilter ||
      (value !== null &&
        (filterRange!.min === null || value >= filterRange!.min) &&
        (filterRange!.max === null || value <= filterRange!.max));
    if (!inRange) {
      // Collapse the instance to a point so it doesn't render and won't be
      // picked up by the raycaster either.
      hiddenMatrix.setPosition(point.position.x, point.position.y, point.position.z);
      mesh.setMatrixAt(i, hiddenMatrix);
    } else {
      matrix.identity();
      matrix.setPosition(point.position.x, point.position.y, point.position.z);
      mesh.setMatrixAt(i, matrix);
    }
    const color = new THREE.Color(getIlluminanceColor(point, visualizationType, minMaxValues));
    mesh.setColorAt(i, color);
  });

  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  return mesh;
};

const MeasurementPointsInstanced = ({
  points,
  mesh,
  activePoint,
  setActivePoint,
}: {
  points: Point[];
  mesh: THREE.InstancedMesh;
  activePoint: Point | null;
  setActivePoint: (point: Point | null) => void;
}) => {
  const meshRef = useRef<THREE.InstancedMesh>(mesh);
  const { camera, raycaster, mouse } = useThree();

  const user = useAppSelector(getUser)!;
  const measurementPointsOnHover = user?.settings?.measurementPointsOnHover ?? false;
  const unitSystem = user.settings.units ?? UnitSystem.METRIC;

  const handlePointerOver = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.stopPropagation();
    if (!meshRef.current) return;

    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObject(meshRef.current);
    if (intersects.length > 0) {
      const instanceId = intersects[0].instanceId;
      if (instanceId !== undefined && (!activePoint || points[instanceId] !== activePoint)) {
        const point = points[instanceId];
        setActivePoint(point);
      }
    }
  };

  const handlePointerOut = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.stopPropagation();
    if (!activePoint) return;

    // Only clear if this point belongs to this mesh
    const isThisMeshPoint = points.some((p) => p === activePoint);
    if (isThisMeshPoint) {
      setActivePoint(null);
    }
  };

  const handleClick = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    // If the mouse has moved more than a small threshold, it's a drag/rotate, not a click
    if (e.delta > 5) return;

    if (!meshRef.current) return;

    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObject(meshRef.current);
    if (intersects.length > 0) {
      const instanceId = intersects[0].instanceId;
      if (instanceId !== undefined) {
        const point = points[instanceId];
        if (activePoint && activePoint === point) {
          setActivePoint(null);
        } else {
          setActivePoint(point);
        }
      }
    } else {
      setActivePoint(null);
    }
  };

  const isCurrentMeshActive = activePoint && points.some((p) => p === activePoint);

  return (
    <>
      <primitive
        ref={meshRef}
        object={mesh}
        onPointerOver={measurementPointsOnHover ? handlePointerOver : () => noop}
        onPointerOut={measurementPointsOnHover ? handlePointerOut : () => noop}
        onPointerMove={measurementPointsOnHover ? handlePointerOver : () => noop}
        onClick={!measurementPointsOnHover ? handleClick : () => noop}
      />
      {isCurrentMeshActive && activePoint && (
        <Html position={[activePoint.position.x, activePoint.position.y, activePoint.position.z]}>
          {/* drei <Html> renders into a portal that doesn't carry the redux Provider
              context, so re-provide the store for the popup's useSelector. */}
          <Provider store={store}>
            <MeasurementPopup point={activePoint} onClose={() => setActivePoint(null)} unitSystem={unitSystem} />
          </Provider>
        </Html>
      )}
    </>
  );
};

type MeasurementPointsProps = {
  data: MeasurementData[];
  /** Optional data the colour scale (min/max) is computed from instead of `data` — lets
   *  measurement-surface points share the SAME scale as the whole-model "All model points". */
  scaleData?: MeasurementData[];
};

const MeasurementPoints = ({ data, scaleData }: MeasurementPointsProps) => {
  const [activePoint, setActivePoint] = useState<Point | null>(null);
  const measurementVisualizationType = useAppSelector((state) => state.workspace.measurementVisualizationType)!;
  const filterRange = useAppSelector((state) => state.workspace.measurementFilterRange);
  const groupedData: GroupedMeasurementData = useMemo(() => parseAndGroupMeasurementData(data), [data]);

  const displayScale = useMemo(
    () => calculateDisplayScale(scaleData && scaleData.length ? scaleData : data, measurementVisualizationType),
    [scaleData, data, measurementVisualizationType],
  );

  const instancedMeshes = useMemo(() => {
    const meshes: { [name: string]: { mesh: THREE.InstancedMesh; points: Point[] } } = {};
    Object.keys(groupedData).forEach((name) => {
      const points = groupedData[name].flatMap((data) => data.points);
      meshes[name] = {
        mesh: makeInstanced(points, measurementVisualizationType, displayScale, filterRange),
        points,
      };
    });
    return meshes;
  }, [groupedData, measurementVisualizationType, displayScale, filterRange]);

  const groupNames = Object.keys(groupedData);

  return (
    <group>
      {groupNames.map((name) => (
        <MeasurementPointsInstanced
          // include the mesh uuid so the primitive remounts whenever the data
          // changes (e.g. grid-spacing recompute), not only on viz-type change.
          key={`${name}-${measurementVisualizationType}-${instancedMeshes[name].mesh.uuid}`}
          points={instancedMeshes[name].points}
          mesh={instancedMeshes[name].mesh}
          activePoint={activePoint}
          setActivePoint={setActivePoint}
        />
      ))}
    </group>
  );
};

export { MeasurementPoints };
